/**
 * Professional Color System for ERP Application
 * 
 * A comprehensive color palette based on your brand colors:
 * - Primary: #05445E (Deep teal/blue)
 * - Secondary: #189AB4 (Bright cyan)
 * - Tertiary: #75E6DA (Soft aqua)
 * - Background: #D4F1F4 (Very light aqua)
 */

// Base Brand Colors
export const BRAND_COLORS = {
  PRIMARY: '#75E6DA',    // Soft aqua - Main brand color (user requested)
  SECONDARY: '#05445E',  // Deep teal/blue - Text/icons and secondary
  TERTIARY: '#189AB4',   // Bright cyan - Tertiary accent
  BACKGROUND: '#D4F1F4', // Very light aqua - Main background
};

// Color Variations (Lighter and Darker versions)
export const COLOR_VARIATIONS = {
  PRIMARY: {
    50: '#F6FBFA',
    100: '#E6F7F4',
    200: BRAND_COLORS.PRIMARY,  // Base (mapped to new primary)
    300: '#4ED8C9',
    400: '#3BC9BA',
    500: '#2AAFA0',
    600: '#1E8A78',
    700: '#146055',
    800: '#0C433A',
    900: '#062B24',
  },
  SECONDARY: {
    50: '#F0EFEF',
    100: '#E1E0E2',
    200: BRAND_COLORS.SECONDARY,  // Base (mapped to new secondary)
    300: '#A5A2A9',
    400: '#918D95',
    500: '#7D7881',
    600: '#69636D',
    700: '#554E59',
    800: '#413945',
    900: '#2D2431',
  },
  TERTIARY: {
    50: '#F3F3F4',
    100: '#E7E8E9',
    200: BRAND_COLORS.TERTIARY,  // Base (mapped to new tertiary)
    300: '#737982',
    400: '#606776',
    500: '#4D556A',
    600: '#3A435E',
    700: '#273152',
    800: '#141F46',
    900: '#010D3A',
  },
  BACKGROUND: {
    50: '#FAFAFA',
    100: '#F5F4F0',
    200: BRAND_COLORS.BACKGROUND,  // Base (mapped to new background)
    300: '#E7E6DE',
    400: '#E0DFD5',
    500: '#D9D8CC',
    600: '#D2D1C3',
    700: '#CBCABA',
    800: '#C4C3B1',
    900: '#BDBCA8',
  },
};

// Semantic Color Categories
export const UI_COLORS = {
  // Brand Colors (direct access to brand colors)
  BRAND: {
    PRIMARY: BRAND_COLORS.PRIMARY,              // Main brand color
    SECONDARY: BRAND_COLORS.SECONDARY,          // Secondary brand color
    TERTIARY: BRAND_COLORS.TERTIARY,           // Tertiary brand color
    BACKGROUND: BRAND_COLORS.BACKGROUND,        // Brand background
  },

  // Background Colors
  BACKGROUNDS: {
    PRIMARY: BRAND_COLORS.BACKGROUND,           // Main app background
    CARD: '#FFFFFF',                            // Card backgrounds
    SIDEBAR: COLOR_VARIATIONS.PRIMARY[100],     // Sidebar background
    HEADER: COLOR_VARIATIONS.SECONDARY[50],     // Header background
    MODAL: 'rgba(255, 255, 255, 0.98)',       // Modal backgrounds
    OVERLAY: 'rgba(0, 0, 0, 0.4)',            // Dark overlay
    SECTION: COLOR_VARIATIONS.BACKGROUND[100],  // Section backgrounds
  },

  // Text Colors
  TEXT: {
    PRIMARY: BRAND_COLORS.SECONDARY,    // Main text/icons color (deep teal)
    SECONDARY: COLOR_VARIATIONS.TERTIARY[700],  // Secondary text
    MUTED: COLOR_VARIATIONS.SECONDARY[400],     // Muted text
    LIGHT: COLOR_VARIATIONS.BACKGROUND[600],    // Light text on dark backgrounds
    WHITE: '#FFFFFF',                           // White text
    BLACK: '#000000',                          // Black text (explicit)
    ACCENT: COLOR_VARIATIONS.PRIMARY[600],      // Accent text
  },

  // Button Colors
  BUTTONS: {
    PRIMARY: {
      background: BRAND_COLORS.PRIMARY,
      text: BRAND_COLORS.SECONDARY,
      hover: COLOR_VARIATIONS.PRIMARY[400],
      active: COLOR_VARIATIONS.PRIMARY[500],
      disabled: COLOR_VARIATIONS.PRIMARY[200],
      loading: {
        background: COLOR_VARIATIONS.PRIMARY[300],
        text: COLOR_VARIATIONS.TERTIARY[600],
      },
      border: 'transparent',
    },
    SECONDARY: {
      background: BRAND_COLORS.SECONDARY,
      text: '#ffffff',
      hover: COLOR_VARIATIONS.SECONDARY[400],
      active: COLOR_VARIATIONS.SECONDARY[500],
      disabled: COLOR_VARIATIONS.SECONDARY[200],
      loading: {
        background: COLOR_VARIATIONS.SECONDARY[300],
        text: '#FFFFFF',
      },
      border: BRAND_COLORS.SECONDARY,
    },
    TERTIARY: {
      background: 'transparent',
      text: BRAND_COLORS.TERTIARY,
      hover: COLOR_VARIATIONS.TERTIARY[100],
      active: COLOR_VARIATIONS.TERTIARY[200],
      disabled: COLOR_VARIATIONS.TERTIARY[200],
    },
    DANGER: {
      background: '#DC2626',
      text: '#FFFFFF',
      hover: {
        background: '#B91C1C',
      },
      active: '#991B1B',
      disabled: '#FCA5A5',
      loading: {
        background: '#EF4444',
        text: '#FFFFFF',
      },
    },
    SUCCESS: {
      background: '#059669',
      text: '#FFFFFF',
      hover: {
        background: '#047857',
      },
      active: '#065F46',
      disabled: '#A7F3D0',
      loading: {
        background: '#10B981',
        text: '#FFFFFF',
      },
    },
  },

  // Border Colors
  BORDERS: {
    LIGHT: COLOR_VARIATIONS.BACKGROUND[400],    // Light borders
    MEDIUM: COLOR_VARIATIONS.SECONDARY[300],    // Medium borders
    STRONG: COLOR_VARIATIONS.TERTIARY[400],     // Strong borders
    FOCUS: BRAND_COLORS.PRIMARY,                // Focus states
    ERROR: '#DC2626',                           // Error borders
    SUCCESS: '#059669',                         // Success borders
  },

  // Form Colors
  FORMS: {
    INPUT: {
      background: '#FFFFFF',
      border: COLOR_VARIATIONS.SECONDARY[300],
      text: COLOR_VARIATIONS.TERTIARY[800],
      placeholder: COLOR_VARIATIONS.SECONDARY[400],
      focus: BRAND_COLORS.PRIMARY,
      error: '#DC2626',
      success: '#059669',
    },
    LABEL: COLOR_VARIATIONS.TERTIARY[700],
    HELP_TEXT: COLOR_VARIATIONS.SECONDARY[500],
  },

  // Status Colors
  STATUS: {
    ERROR: '#DC2626',
    WARNING: '#D97706',
    SUCCESS: '#059669',
    INFO: '#0284C7',
    PENDING: COLOR_VARIATIONS.PRIMARY[500],
  },

  // Navigation Colors
  NAVIGATION: {
    ACTIVE: BRAND_COLORS.TERTIARY,
    INACTIVE: COLOR_VARIATIONS.SECONDARY[400],
    HOVER: COLOR_VARIATIONS.PRIMARY[200],
    SELECTED: COLOR_VARIATIONS.PRIMARY[100],
  },

  // Shadow Colors
  SHADOWS: {
    LIGHT: 'rgba(134, 139, 142, 0.1)',
    MEDIUM: 'rgba(134, 139, 142, 0.15)',
    STRONG: 'rgba(134, 139, 142, 0.25)',
    COLORED: `rgba(117, 230, 218, 0.12)`, // Using new primary color (#75E6DA)
  },
};

// Gradient Definitions
export const GRADIENTS = {
  PRIMARY: `linear-gradient(135deg, ${BRAND_COLORS.PRIMARY} 0%, ${COLOR_VARIATIONS.PRIMARY[400]} 100%)`,
  SECONDARY: `linear-gradient(135deg, ${BRAND_COLORS.SECONDARY} 0%, ${COLOR_VARIATIONS.SECONDARY[400]} 100%)`,
  NEUTRAL: `linear-gradient(135deg, ${BRAND_COLORS.BACKGROUND} 0%, ${COLOR_VARIATIONS.BACKGROUND[300]} 100%)`,
  WARM: `linear-gradient(135deg, ${COLOR_VARIATIONS.PRIMARY[200]} 0%, ${COLOR_VARIATIONS.PRIMARY[300]} 100%)`,
  COOL: `linear-gradient(135deg, ${COLOR_VARIATIONS.SECONDARY[200]} 0%, ${COLOR_VARIATIONS.TERTIARY[200]} 100%)`,
};

// Helper Functions
export const colorHelpers = {
  /**
   * Convert hex color to RGB values
   * @param {string} hex - Hex color code
   * @returns {object} RGB values
   */
  hexToRgb: (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  },

  /**
   * Create rgba color with opacity
   * @param {string} hex - Hex color code
   * @param {number} opacity - Opacity value (0-1)
   * @returns {string} RGBA color string
   */
  withOpacity: (hex, opacity) => {
    const rgb = colorHelpers.hexToRgb(hex);
    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})` : hex;
  },

  /**
   * Get appropriate text color for background
   * @param {string} backgroundColor - Background hex color
   * @returns {string} Text color (light or dark)
   */
  getTextColor: (backgroundColor) => {
    const rgb = colorHelpers.hexToRgb(backgroundColor);
    if (!rgb) return UI_COLORS.TEXT.PRIMARY;
    
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 128 ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.WHITE;
  },
};

// Dark Mode Support (if needed in the future)
export const DARK_MODE_COLORS = {
  BACKGROUND: '#1F1F1F',
  CARD: '#2A2A2A',
  TEXT: '#FFFFFF',
  TEXT_SECONDARY: '#B3B3B3',
  BORDER: '#404040',
  PRIMARY: COLOR_VARIATIONS.PRIMARY[300], // Lighter version for dark mode
  SECONDARY: COLOR_VARIATIONS.SECONDARY[300],
};

// Export default color system
const COLORS = {
  BRAND_COLORS,
  COLOR_VARIATIONS,
  UI_COLORS,
  GRADIENTS,
  colorHelpers,
  DARK_MODE_COLORS,
};

export default COLORS;